import Spinner from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import useAddCollectionOrder from '@/hooks/collectionOrder/useAddCollectionOrder';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';


import CommuneDropdown from '@/components/CommuneDropdown';
import WilayaDropdown from '@/components/WilayaDropdown';
import { CustomerDropdown } from '@/pages/collectionOrder/addCollectionOrder/CustomerDropdown';
import TimePicker from '@/pages/collectionOrder/addCollectionOrder/TimePicker';
import { formatNumberWithDotAndComma } from '@/utils/moneyFormatter';


type PropsAddEmployee = {
    onAddCollectionOrder: () => void;
    isMobile?: boolean;
};

const AddCollection: React.FC<PropsAddEmployee> = ({
    onAddCollectionOrder,
    isMobile = false,
}) => {
    const [open, setOpen] = useState(false);
    const [customer, setCustomer] = useState<Client>({} as Client);
    const [collector, setCollector] = useState<Employee>({} as Employee);
    const [time, setTime] = useState<string | null>(null);
    const [totalBalance, setTotalBalance] = useState('');
    const [amount, setAmount] = useState(0);
    const [selectedWilaya, setSelectedWilaya] = useState<string>('');

    const [isPhoneEdited, setIsPhoneEdited] = useState(false);
    const [isLocationEdited, setIsLocationEdited] = useState(false);
    const [isTotalBalanceEdited, setIsTotalBalanceEdited] = useState(false);
    const [showMoreInfo, setShowMoreInfo] = useState(false);

    const handleTotalBalanceBlur = () => {
        const formattedValue = formatNumberWithDotAndComma(totalBalance);
        setTotalBalance(formattedValue);
        addCollectionOrderForm.setValue('totalBalance', formattedValue);
    };


    const handleAmountBlur = () => {
        const formattedValue = formatNumberWithDotAndComma(amount.toString());
        setAmount(amount);
        addCollectionOrderForm.setValue('amount', amount);
    };

    const { isSubmitting, addCollectionOrderForm, onSubmit } =
        useAddCollectionOrder();


    // Prefill phone and location only if the user has not edited the phone field
    useEffect(() => {
        const parsedObject = JSON.parse(localStorage.getItem('authData') || "");

        addCollectionOrderForm.setValue(
            'collectorId',
            +parsedObject.user.id
        );
        setCollector(parsedObject.user);
        if (!isPhoneEdited && customer?.phone) {
            addCollectionOrderForm.setValue('phone', customer.phone);
        }
        if (!isLocationEdited && customer?.positionGps) {
            addCollectionOrderForm.setValue('location', customer.positionGps);
        }

        if (customer && customer.wilaya) {
            addCollectionOrderForm.setValue('wilaya', customer.wilaya);
        }

        if (customer && customer.commune) {
            addCollectionOrderForm.setValue('commune', customer.commune);
        }

        if (!isTotalBalanceEdited && customer?.totalBalance) {
            addCollectionOrderForm.setValue('totalBalance', customer.totalBalance);
        }
    }, [
        customer,
        isPhoneEdited,
        isLocationEdited,
        isTotalBalanceEdited,
        addCollectionOrderForm,
    ]);
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('Phone changed, setting isPhoneEdited to true');
        setIsPhoneEdited(true); // Mark as edited when the user types
        addCollectionOrderForm.setValue('phone', e.target.value);
    };

    const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('Location changed, setting isLocationEdited to true');
        setIsLocationEdited(true); // Mark as edited when the user types
        addCollectionOrderForm.setValue('location', e.target.value);
    };

    const handleTotalBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('Total balance changed, setting isTotalBalanceEdited to true');
        setIsTotalBalanceEdited(true); // Mark as edited when the user types
        addCollectionOrderForm.setValue('totalBalance', e.target.value);
    };

    const handleSubmit = async (values: CollectionOrder) => {
        try {
            await onSubmit(
                values,
                (message) => {
                    toast.success(message);
                    setOpen(false);
                    onAddCollectionOrder();
                },
                (message) => toast.error(message)
            );
        } catch (error) {
            console.error('Submission error:', error);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger
                className={`
      ${isMobile
                        ? 'px-0 text-[14px] border border-primary bg-[#EAF3FF] text-primary w-full rounded-lg  '
                        : 'px-4 text-[16px] text-white bg-primary rounded-md hover:bg-primary/90 transition ease-in-out duration-200'
                    }
       py-2  font-normal gap-3`}
            >
                New Collection order
            </DialogTrigger>
            <DialogContent className='p-8 md:max-w-3xl'>
                <DialogHeader>
                    <DialogTitle className='font-medium text-[18px]'>
                        Add Collection order
                    </DialogTitle>
                    <DialogDescription>
                        <Form {...addCollectionOrderForm}>
                            <form
                                onSubmit={addCollectionOrderForm.handleSubmit(handleSubmit)}
                                className='flex flex-col mt-4 gap-4 h-[calc(100vh-200px)] md:h-full text-black overflow-y-scroll'
                            >
                                {/* CUSTOMER AND COLLECTOR */}
                                <div className='flex flex-col gap-4 md:flex-row md:gap-8'>
                                    <div className='md:w-1/2'>
                                        <CustomerDropdown
                                            addCommandeForm={addCollectionOrderForm}
                                            setCustomer={setCustomer}
                                        />
                                    </div>
                                    <div className='md:w-1/2'>
                                        <FormField
                                            //control={addCollectionOrderForm.control}
                                            name='Collector'
                                            render={({ field }) => (
                                                <FormItem className='w-full'>
                                                    <FormControl>
                                                        <div className='relative'>
                                                            <p className='absolute top-[25%] right-2 text-primary'>
                                                                Collector
                                                            </p>
                                                            <Input
                                                                disabled
                                                                className='pr-28'
                                                                value={collector.fullname}

                                                            />
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* wilaya and date */}
                                <div className='flex flex-col gap-4 md:flex-row md:gap-8'>
                                    <FormField
                                        control={addCollectionOrderForm.control}
                                        name='wilaya'
                                        render={({ field }) => (
                                            <FormItem className='md:w-1/2'>
                                                <FormControl>
                                                    <WilayaDropdown
                                                        onValueChange={(value) => {
                                                            field.onChange(value);
                                                            setSelectedWilaya(field.value);
                                                        }}
                                                        value={field.value || customer?.wilaya || ''}
                                                    />
                                                </FormControl>

                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={addCollectionOrderForm.control}
                                        name='date'
                                        render={({ field }) => (
                                            <FormItem className='flex flex-col md:w-1/2'>
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <FormControl>
                                                            <Button
                                                                variant={'outline'}
                                                                className={cn(
                                                                    'bg-white pr-2 h-[44px] border-muted-foreground text-black w-full pl-3 text-left font-normal',
                                                                    !field.value && 'text-muted-foreground'
                                                                )}
                                                            >
                                                                {field.value ? (
                                                                    format(field.value, 'PPP')
                                                                ) : (
                                                                    <span>Pick a date</span>
                                                                )}
                                                                <CalendarIcon className='w-4 h-4 ml-auto opacity-50' />
                                                            </Button>
                                                        </FormControl>
                                                    </PopoverTrigger>
                                                    <PopoverContent className='w-auto p-0' align='start'>
                                                        <Calendar
                                                            mode='single'
                                                            selected={field.value}
                                                            onSelect={field.onChange}
                                                            disabled={(date) => date < new Date('1900-01-01')}
                                                            initialFocus
                                                        />
                                                    </PopoverContent>
                                                </Popover>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                {/* total balance and amount */}
                                <div className='flex flex-col gap-4 md:flex-row md:gap-8'>
                                    <FormField
                                        control={addCollectionOrderForm.control}
                                        name='amount'
                                        rules={{ required: 'Amount is required' }}
                                        render={({ field }) => (
                                            <FormItem className='w-full'>
                                                <FormControl>
                                                    <div className='relative'>
                                                        <p className='absolute top-[25%] right-2 text-primary'>
                                                            Amount to collect
                                                        </p>
                                                        <Input
                                                            className='pr-28'
                                                            placeholder='00,00 DZD'
                                                            value={amount}
                                                            onChange={(e) =>
                                                                setAmount(Number(e.target.value))
                                                            } // Keep track of user input
                                                            onBlur={handleAmountBlur} // Format when input loses focus
                                                        />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <Button
                                    type='button'
                                    variant='ghost'
                                    className='flex justify-end p-0 text-muted-foreground'
                                    onClick={() => setShowMoreInfo(!showMoreInfo)}
                                >
                                    {showMoreInfo ? (
                                        <>
                                            Hide additional info
                                            <ChevronUpIcon className='w-4 h-4 ml-2' />
                                        </>
                                    ) : (
                                        <>
                                            Show additional info
                                            <ChevronDownIcon className='w-4 h-4 ml-2' />
                                        </>
                                    )}
                                </Button>

                                {showMoreInfo && (
                                    <div className='flex flex-col gap-4 mt-4'>
                                        <div className='flex flex-col gap-4 md:flex-row md:gap-8'>
                                            <FormField
                                                control={addCollectionOrderForm.control}
                                                name='totalBalance'
                                                // rules={{ required: 'Total Balance is required' }}
                                                render={({ field }) => (
                                                    <FormItem className='md:w-1/2'>
                                                        <FormControl>
                                                            <div className='relative'>
                                                                <p className='absolute top-[25%] left-2 text-primary'>
                                                                    Total Balance
                                                                </p>
                                                                <Input
                                                                    className='pl-28'
                                                                    {...field}
                                                                    value={
                                                                        isTotalBalanceEdited
                                                                            ? field.value
                                                                            : customer?.totalBalance || ''
                                                                    } // Use field.value if phone is edited
                                                                    onChange={handleTotalBalanceChange} // Track input changes and mark as edited
                                                                    placeholder='00,00 DZD'
                                                                />
                                                            </div>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={addCollectionOrderForm.control}
                                                name='commune'
                                                render={({ field }) => (
                                                    <FormItem className='md:w-1/2'>
                                                        <FormControl>
                                                            <CommuneDropdown
                                                                wilaya={addCollectionOrderForm.getValues(
                                                                    'wilaya'
                                                                )}
                                                                onValueChange={field.onChange}
                                                                defaultValue={
                                                                    field.value || customer?.commune || ''
                                                                }
                                                                placeholder='Commune'
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className='flex flex-col gap-4 md:flex-row md:gap-8'>
                                            <TimePicker
                                                value={addCollectionOrderForm.getValues('hour')}
                                                onValueChange={(value: string) => {
                                                    setTime(value);
                                                    addCollectionOrderForm.setValue('hour', value);
                                                }}
                                            />
                                            <FormField
                                                control={addCollectionOrderForm.control}
                                                name='phone'
                                                rules={{ required: 'Phone is required' }}
                                                render={({ field }) => (
                                                    <FormItem className='md:w-1/2'>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                value={
                                                                    isPhoneEdited
                                                                        ? field.value
                                                                        : customer?.phone || ''
                                                                } // Use field.value if phone is edited
                                                                onChange={handlePhoneChange} // Track input changes and mark as edited
                                                                placeholder='Phone number'
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className='flex flex-col gap-4 md:flex-row md:gap-8'>
                                            <FormField
                                                control={addCollectionOrderForm.control}
                                                name='location'
                                                render={({ field }) => (
                                                    <FormItem className='w-full'>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                value={
                                                                    isLocationEdited
                                                                        ? field.value
                                                                        : customer?.positionGps || ''
                                                                }
                                                                onChange={handleLocationChange}
                                                                placeholder='33.3384687308, 67.87692730287'
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </div>
                                )}

                                <DialogFooter className='flex w-full mt-4'>
                                    <div className='mx-auto mt-8 mb-6'>
                                        <Button
                                            disabled={isSubmitting}
                                            className='px-20 '
                                            type='submit'
                                        >
                                            {isSubmitting ? <Spinner /> : <p>Save</p>}
                                        </Button>
                                    </div>
                                </DialogFooter>
                            </form>
                        </Form>
                    </DialogDescription>
                </DialogHeader>
            </DialogContent>
        </Dialog>
    );
};

export default AddCollection;
